import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import {
  Alert,
  Box,
  Card,
  TextField,
  Button,
  Stack,
  Typography,
  Container,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Logo } from '@/components/Logo';
import { login, isAuthenticated } from '@/features/auth';

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      await navigate({ to: '/mailbox' });
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      setError(status === 401 ? t('mail.login.invalidCredentials') : t('mail.login.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 64px)',
      }}
    >
      <Card
        sx={{
          width: '100%',
          padding: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Stack spacing={4}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Logo size="large" />
          </Box>

          <Stack spacing={1} sx={{ textAlign: 'center' }}>
            <Typography variant="h1" component="h1">
              {t('mail.login.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('mail.login.subtitle')}
            </Typography>
          </Stack>

          <form onSubmit={handleLogin}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                fullWidth
                label={t('mail.login.email')}
                placeholder={t('mail.login.emailPlaceholder')}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                variant="outlined"
                size="medium"
              />

              <TextField
                fullWidth
                label={t('mail.login.password')}
                placeholder={t('mail.login.passwordPlaceholder')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                variant="outlined"
                size="medium"
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || !email || !password}
              >
                {submitting ? t('mail.login.submitting') : t('mail.login.submit')}
              </Button>
            </Stack>
          </form>

          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
            {t('mail.login.needsAuth')}
          </Typography>
        </Stack>
      </Card>
    </Container>
  );
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/mailbox' });
    }
  },
  component: LoginPage,
});
